import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import CaseDocumentsPane from "@/components/CaseDocumentsPane";
import CaseDraftPane from "@/components/CaseDraftPane";
import CaseKnowledgePane from "@/components/CaseKnowledgePane";
import ChatPane from "@/components/ChatPane";
import ConversationHistoryPane from "@/components/ConversationHistoryPane";
import DocumentViewer from "@/components/DocumentViewer";
import { updateCase } from "@/lib/api";
import { invalidateCase } from "@/lib/cache/invalidate";
import { useCase, useDocuments, useModels } from "@/hooks/queries";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLang } from "@/lib/lang";
import { useAuth } from "@/lib/auth";
import { findCountry, STATIC_COUNTRIES } from "@/components/CountrySelect";
import { toast } from "sonner";
import { ArrowLeft, History, Settings2 } from "lucide-react";

const PRACTICE_KEYS = {
  civil: "practice_civil",
  criminal: "practice_criminal",
};

export default function CaseWorkspace() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const defaultJurisdiction = findCountry(STATIC_COUNTRIES, user?.operating_country || "JO")?.default_jurisdiction || "";
  const [viewerDocId, setViewerDocId] = useState(null);
  const [activeSection, setActiveSection] = useState("chat");
  const [model, setModel] = useState("gemini-2.5-flash-lite");
  const [conversationId, setConversationId] = useState(null);
  const [showMobileHistory, setShowMobileHistory] = useState(false);
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);

  const { data: caseData, isLoading: caseLoading, error: caseError } = useCase(caseId);
  const { data: libraryDocs = [] } = useDocuments();
  const { data: modelsData } = useModels();

  useEffect(() => {
    if (modelsData?.default) setModel(modelsData.default);
  }, [modelsData?.default]);

  useEffect(() => {
    if (caseError) {
      toast.error(caseError.message);
      navigate("/workspace/cases");
    }
  }, [caseError, navigate]);

  const caseDocIds = useMemo(
    () => (caseData?.documents || []).map((d) => d.id),
    [caseData?.documents],
  );

  const handleArchive = async () => {
    if (!window.confirm(t("case_archive"))) return;
    try {
      await updateCase(caseId, { status: "archived" });
      toast.success(t("case_saved"));
      await invalidateCase(undefined, caseId);
      navigate("/workspace/cases");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleCaseRefresh = () => invalidateCase(undefined, caseId);

  const historyPane = (
    <ConversationHistoryPane
      caseId={caseId}
      activeConversationId={conversationId}
      onSelectConversation={(conv) => {
        setConversationId(conv.id);
        setShowMobileHistory(false);
      }}
      onNewConversation={() => {
        setConversationId(null);
        setShowMobileHistory(false);
      }}
    />
  );

  if (caseLoading || !caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <span className="text-sm text-slate-500">{t("streaming")}</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header
        activeTab="cases"
        onTabChange={(tab) => {
          if (tab === "cases") {
            navigate("/workspace/cases");
          } else if (tab === "research") {
            navigate("/workspace/research");
          }
        }}
      />

      <div className="grid-border-b px-6 py-3 bg-white flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={() => navigate("/workspace/cases")}
          className="flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={14} />
          {t("case_back")}
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-serif-display text-xl text-slate-900 truncate">{caseData.title}</h1>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {t(PRACTICE_KEYS[caseData.practice_area] || caseData.practice_area)}
          </div>
        </div>
        <div className="flex gap-0 grid-border h-9">
          {["documents", "draft", "chat"].map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveSection(key)}
              className={[
                "px-4 text-xs transition-colors",
                activeSection === key ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {key === "documents" && t("case_documents")}
              {key === "draft" && t("case_draft")}
              {key === "chat" && t("case_chat")}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setAdvancedSettingsOpen(true)}
          className="h-9 px-3 grid-border flex items-center gap-2 text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900"
          aria-label={t("case_advanced_settings")}
        >
          <Settings2 size={14} />
          <span className="hidden sm:inline">{t("case_advanced_settings")}</span>
        </button>
        <button
          type="button"
          onClick={handleArchive}
          className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900"
        >
          {t("case_archive")}
        </button>
      </div>

      <main className="flex-1 h-[calc(100vh-128px)] overflow-hidden">
        {activeSection === "documents" && (
          <div className="h-full flex flex-col">
            {["pending", "in_progress"].includes(caseData.onboarding_status) && (
              <button
                type="button"
                onClick={() => setAdvancedSettingsOpen(true)}
                className="grid-border-b bg-amber-50/70 px-5 py-3 text-start flex items-center justify-between gap-4 hover:bg-amber-50"
              >
                <span className="text-xs text-slate-700">
                  {t("case_onboarding_settings_prompt")}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-slate-900 shrink-0">
                  {t("case_open_advanced_settings")}
                </span>
              </button>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] min-h-0 flex-1">
              <aside className="grid-border-e h-full overflow-hidden">
                <CaseDocumentsPane
                  caseId={caseId}
                  documents={caseData.documents || []}
                  libraryDocuments={libraryDocs}
                  selectedId={viewerDocId}
                  onSelect={(doc) => setViewerDocId(doc.id)}
                />
              </aside>
              <section className="h-full overflow-hidden">
                {viewerDocId ? (
                  <DocumentViewer documentId={viewerDocId} />
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-slate-400">
                    {t("select_document")}
                  </div>
                )}
              </section>
            </div>
          </div>
        )}

        {activeSection === "draft" && (
          <CaseDraftPane
            caseId={caseId}
            caseData={caseData}
            model={model}
            defaultJurisdiction={defaultJurisdiction}
            onRefresh={handleCaseRefresh}
          />
        )}

        {activeSection === "chat" && (
          <div className="flex h-full overflow-hidden">
            <aside className="hidden lg:block w-[240px] flex-shrink-0 grid-border-e overflow-hidden">
              {historyPane}
            </aside>
            {showMobileHistory && (
              <aside className="lg:hidden absolute inset-y-0 start-0 z-20 w-[260px] grid-border-e bg-white shadow-lg overflow-hidden">
                {historyPane}
              </aside>
            )}
            <div className="flex-1 min-w-0 overflow-hidden relative">
              <button
                type="button"
                onClick={() => setShowMobileHistory((v) => !v)}
                className="lg:hidden absolute top-3 start-3 z-10 h-8 w-8 grid-border bg-white flex items-center justify-center text-slate-600"
              >
                <History size={14} />
              </button>
              <ChatPane
                documents={caseData.documents || libraryDocs.filter((d) => caseDocIds.includes(d.id))}
                chatContextDocIds={caseDocIds}
                caseId={caseId}
                hideContextPicker
                allowEmptyContext
                model={model}
                conversationId={conversationId}
                onConversationCreated={setConversationId}
              />
            </div>
          </div>
        )}
      </main>

      <Dialog open={advancedSettingsOpen} onOpenChange={setAdvancedSettingsOpen}>
        <DialogContent className="rounded-none grid-border p-0 gap-0 max-w-3xl h-[85vh] overflow-hidden">
          <DialogTitle className="sr-only">{t("case_advanced_settings")}</DialogTitle>
          <CaseKnowledgePane
            caseData={caseData}
            model={model}
            onUpdated={handleCaseRefresh}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
